import apiClient from './client';

// Agent 定时任务调度（P8）：声明式 cron 任务 + 运行记录查询

export type ScheduleTaskType = 'eval_regression' | 'ct_feedback_collect';
export type ScheduleRunStatus = 'running' | 'success' | 'failed' | 'interrupted' | 'skipped';

export interface AgentSchedule {
  id: string;
  agent_id: string;
  task_type: string;
  cron: string;
  payload: Record<string, unknown>;
  enabled: boolean;
  last_run_at: string | null;
  next_run_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface AgentScheduleRun {
  id: string;
  schedule_id: string;
  agent_id: string;
  trigger: 'scheduled' | 'manual';
  status: ScheduleRunStatus;
  started_at: string | null;
  finished_at: string | null;
  result_summary: Record<string, unknown> | null;
  error: string | null;
}

export const TASK_TYPE_LABELS: Record<string, string> = {
  eval_regression: '评测回归',
  ct_feedback_collect: 'CT 反馈采集',
};

export const RUN_STATUS_LABELS: Record<string, string> = {
  running: '运行中',
  success: '成功',
  failed: '失败',
  interrupted: '已中断',
  skipped: '已跳过',
};

export async function listSchedules(agentId: string): Promise<AgentSchedule[]> {
  const res = await apiClient.get(`/agents/${agentId}/schedules`);
  return res.data;
}

export async function createSchedule(
  agentId: string,
  body: { task_type: string; cron: string; payload?: Record<string, unknown>; enabled?: boolean }
): Promise<AgentSchedule> {
  const res = await apiClient.post(`/agents/${agentId}/schedules`, body);
  return res.data;
}

export async function updateSchedule(
  scheduleId: string,
  body: Partial<{ task_type: string; cron: string; payload: Record<string, unknown>; enabled: boolean }>
): Promise<AgentSchedule> {
  const res = await apiClient.patch(`/schedules/${scheduleId}`, body);
  return res.data;
}

export async function deleteSchedule(scheduleId: string): Promise<void> {
  await apiClient.delete(`/schedules/${scheduleId}`);
}

export async function triggerSchedule(scheduleId: string): Promise<AgentScheduleRun> {
  const res = await apiClient.post(`/schedules/${scheduleId}/trigger`);
  return res.data;
}

export async function listScheduleRuns(scheduleId: string, limit = 20): Promise<AgentScheduleRun[]> {
  const res = await apiClient.get(`/schedules/${scheduleId}/runs`, { params: { limit } });
  return res.data;
}

// ------------------------------------------------------------------
// cron ↔ 人话映射
// ------------------------------------------------------------------

export type SchedulePreset = 'daily' | 'weekly' | 'monthly' | 'custom';

const WEEKDAY_LABELS = ['日', '一', '二', '三', '四', '五', '六'];

/** 把档位 + 时间翻译成 5 段 cron 表达式（UTC 口径与后端一致）。"09:00" + 每天 → "0 9 * * *" */
export function buildCron(preset: Exclude<SchedulePreset, 'custom'>, time: string, weekday = 1, dayOfMonth = 1): string {
  const [hh = '9', mm = '0'] = time.split(':');
  const hour = String(Math.min(23, Math.max(0, parseInt(hh, 10) || 0)));
  const minute = String(Math.min(59, Math.max(0, parseInt(mm, 10) || 0)));
  if (preset === 'daily') return `${minute} ${hour} * * *`;
  if (preset === 'weekly') return `${minute} ${hour} * * ${weekday}`;
  return `${minute} ${hour} ${dayOfMonth} * *`;
}

/** 把时间（UTC）展示为本地可读格式 "HH:MM" */
function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** 把可识别的 cron 翻译回人话；无法识别的原样显示。 */
export function describeCron(cron: string): string {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return cron;
  const [minute, hour, dom, month, dow] = parts;
  const m = /^\d{1,2}$/.test(minute) ? parseInt(minute, 10) : NaN;
  const h = /^\d{1,2}$/.test(hour) ? parseInt(hour, 10) : NaN;
  if (Number.isNaN(m) || Number.isNaN(h)) return cron;
  const time = `${pad2(h)}:${pad2(m)}`;

  if (month !== '*') return cron;

  if (dom === '*' && dow === '*') return `每天 ${time}`;
  if (dom === '*' && /^\d$/.test(dow)) {
    const d = parseInt(dow, 10) % 7;
    return `每周${WEEKDAY_LABELS[d]} ${time}`;
  }
  if (/^\d{1,2}$/.test(dom) && dow === '*') return `每月 ${parseInt(dom, 10)} 号 ${time}`;
  return cron;
}
